import React, { useEffect, useRef, useState } from "react";
import { Spinner } from "@material-tailwind/react";

/** Safari/iOS aún no trae getOrInsertComputed; pdf.js moderno lo usa. */
function polyfillMapInsert() {
  const patch = (Proto) => {
    if (!Proto || typeof Proto.getOrInsertComputed === "function") return;
    // eslint-disable-next-line no-param-reassign
    Proto.getOrInsertComputed = function getOrInsertComputed(key, callback) {
      if (this.has(key)) return this.get(key);
      const value = callback(key);
      this.set(key, value);
      return value;
    };
  };
  patch(Map.prototype);
  patch(WeakMap.prototype);
  if (typeof Promise.withResolvers !== "function") {
    Promise.withResolvers = function withResolvers() {
      let resolve;
      let reject;
      const promise = new Promise((res, rej) => {
        resolve = res;
        reject = rej;
      });
      return { promise, resolve, reject };
    };
  }
}

/**
 * PDF página a página al ancho del contenedor (sin recorte lateral en mobile/iOS).
 * Usa build legacy de pdf.js para Safari.
 */
export default function PdfFitViewer({ pdfUrl, title }) {
  const hostRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pageCount, setPageCount] = useState(0);

  useEffect(() => {
    if (!pdfUrl || !hostRef.current) return undefined;
    let cancelled = false;
    let resizeTimer;
    const host = hostRef.current;
    let doc = null;

    const renderAll = async () => {
      if (!hostRef.current || cancelled || !doc) return;
      host.innerHTML = "";
      const width = Math.max(hostRef.current.clientWidth || window.innerWidth, 280);
      for (let n = 1; n <= doc.numPages; n += 1) {
        if (cancelled) return;
        const page = await doc.getPage(n);
        const base = page.getViewport({ scale: 1 });
        const scale = width / base.width;
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d", { alpha: false });
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        canvas.style.display = "block";
        canvas.style.width = "100%";
        canvas.style.height = "auto";
        canvas.style.marginBottom = "8px";
        canvas.setAttribute("aria-label", `${title || "PDF"} — página ${n}`);
        host.appendChild(canvas);
        await page.render({ canvasContext: ctx, viewport }).promise;
      }
      if (!cancelled) {
        setPageCount(doc.numPages);
        setLoading(false);
      }
    };

    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        renderAll().catch(() => {});
      }, 200);
    };

    (async () => {
      setLoading(true);
      setError("");
      setPageCount(0);
      host.innerHTML = "";
      try {
        polyfillMapInsert();
        // Legacy build: evita APIs modernas que rompen Safari/iOS.
        const pdfjs = await import("pdfjs-dist/legacy/build/pdf.mjs");
        const workerSrc = `${window.location.origin}${
          process.env.PUBLIC_URL || ""
        }/pdf.worker.legacy.min.mjs`;
        pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;

        try {
          doc = await pdfjs.getDocument({
            url: pdfUrl,
            withCredentials: false,
            disableRange: true,
            disableStream: true,
          }).promise;
        } catch (firstErr) {
          console.warn("pdf.js load failed, retry", firstErr);
          doc = await pdfjs.getDocument({
            url: pdfUrl,
            withCredentials: false,
            disableRange: true,
            disableStream: true,
            disableAutoFetch: true,
          }).promise;
        }
        if (cancelled) return;
        await renderAll();
        window.addEventListener("resize", onResize);
        window.addEventListener("orientationchange", onResize);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          const raw = String(err?.message || err || "");
          setError(
            raw === "Load failed" ||
              /Failed to fetch|NetworkError|getOrInsertComputed/i.test(raw)
              ? "No se pudo cargar el PDF en este navegador. Ábrelo en una pestaña."
              : raw || "No se pudo mostrar el PDF"
          );
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(resizeTimer);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
      host.innerHTML = "";
    };
  }, [pdfUrl, title]);

  return (
    <div className="relative w-full h-full min-h-0 min-w-0 overflow-x-hidden overflow-y-auto overscroll-contain bg-[#525659]">
      {loading ? (
        <div className="sticky top-0 z-10 flex items-center justify-center gap-2 py-8 text-white/85 bg-[#525659]/80">
          <Spinner className="h-6 w-6" />
          <span className="text-sm">Cargando PDF…</span>
        </div>
      ) : null}
      {error ? (
        <div className="flex flex-col items-center justify-center gap-3 px-4 py-12 text-center">
          <p className="text-sm text-red-200 break-words max-w-md">{error}</p>
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-white underline"
          >
            Abrir PDF en una pestaña
          </a>
        </div>
      ) : null}
      <div ref={hostRef} className="w-full max-w-full" />
      {!loading && !error && pageCount > 0 ? (
        <p className="text-center text-xs text-white/50 py-3">{pageCount} páginas</p>
      ) : null}
    </div>
  );
}
